/**
 * D-EZ-109 — THE WEEK AS THE WIRE KNOWS IT: everything through race N, cut.
 *
 * Built once per Wire. The league's seasons are cut at race N of season S
 * (`cutSeasons`: later seasons dropped, later races dropped, no champion yet,
 * no winter) BEFORE anything reads them, and the sponsor books are filtered to
 * race N, so no section can see race N+1 even by accident. Next week's race
 * comes from the calendar, which was printed before the season began.
 */

import { cutSeasons, peopleContext, type PeopleContext } from '../people/context.ts';
import type { HistorySeason } from '../history/source.ts';
import type { SeasonRaceResult } from '../seasonPlay.ts';
import type { ScheduledRace } from '../schedule.ts';
import type { SeasonStandings } from '../standings.ts';
import type { DriverFinanceState, SponsorChange } from '../finance/raceFinance.ts';
import { shortTrack, trackInProse } from '../paper/fallbackPaper.ts';
import { surname } from '../people/quotes.ts';
import { outAfterRace, type OutReport } from './injuries.ts';
import { playoffPicture, type PlayoffPicture } from './playoff.ts';
import type { WireLeague } from './source.ts';
import type { CareerPrior } from '../history/types.ts';

export interface WeekContext {
  /** This week: league, season, race. Hash with it for a choice that is this week's alone. */
  key: string;
  /** This season: league and season. Hash with it plus `occurrence: raceNumber` to ROTATE phrasings week to week. */
  seasonKey: string;
  seasonNumber: number;
  raceNumber: number;
  /** Every season of the league, cut at race N. */
  seasons: HistorySeason[];
  season: HistorySeason;
  run: SeasonRaceResult;
  /** The season's races through N, oldest first. */
  races: SeasonRaceResult[];
  table: SeasonStandings;
  prevTable: SeasonStandings | null;
  next: ScheduledRace | null;
  schedule: readonly ScheduledRace[];
  people: PeopleContext;
  /** The rivalry book a week earlier (null before race 2 of the league). */
  peoplePrev: PeopleContext | null;
  out: OutReport;
  /** Out next week, dead, or lost to the season — not on next week's entry list. */
  unavailable: Set<string>;
  /** Dead or out for the rest of the year — off the power rankings. */
  lost: Set<string>;
  picture: PlayoffPicture | null;
  contracts: Readonly<Record<string, DriverFinanceState>>;
  /** Race N's sponsor comings and goings, when the season still holds them. */
  sponsorChanges: readonly (SponsorChange & { raceNumber: number })[];
  sponsorChangesHeld: boolean;
  booksSettled: boolean;
  nameOf: (id: string) => string;
  short: (id: string) => string;
  track: string;
  trackShort: string;
  teamOf: (id: string) => string | null;
  sponsorOf: (id: string) => string | null;
  priors: Readonly<Record<string, CareerPrior>>;
}

export function weekContext(league: WireLeague, seasonNumber: number, raceNumber: number): WeekContext | null {
  const ws = league.seasons.find((w) => w.history.seasonNumber === seasonNumber);
  if (!ws) return null;
  const through = { seasonNumber, raceNumber };
  const seasons = cutSeasons(
    league.seasons.map((w) => w.history),
    through,
  );
  const season = seasons.find((s) => s.seasonNumber === seasonNumber);
  if (!season) return null;
  const races = [...season.season.races].sort((a, b) => a.raceNumber - b.raceNumber);
  const run = races.find((r) => r.raceNumber === raceNumber);
  if (!run || !run.standings || run.standings.drivers.length === 0) return null;
  const table = run.standings;
  const prevRun = races.find((r) => r.raceNumber === raceNumber - 1);
  const prevTable = prevRun?.standings && prevRun.standings.drivers.length > 0 ? prevRun.standings : null;
  const next = ws.schedule.find((r) => r.raceNumber === raceNumber + 1) ?? null;

  const people = peopleContext({ archive: seasons, throughRace: through });
  const firstSeason = seasons[0]?.seasonNumber ?? seasonNumber;
  const prevPoint =
    raceNumber > 1
      ? { seasonNumber, raceNumber: raceNumber - 1 }
      : seasonNumber > firstSeason
        ? { seasonNumber: seasonNumber - 1, raceNumber: 999 }
        : null;
  const peoplePrev = prevPoint
    ? peopleContext({ archive: cutSeasons(seasons, prevPoint), throughRace: prevPoint, leagueKey: people.leagueKey })
    : null;

  const out = outAfterRace(season, raceNumber);
  const dead = new Set<string>();
  for (const s of seasons) for (const f of s.season.fatalities ?? []) dead.add(f.driverId);
  for (const d of out.died) dead.add(d.driverId);
  const lost = new Set<string>([...dead, ...out.out.filter((o) => o.racesLeft === 'season').map((o) => o.driverId)]);
  const unavailable = new Set<string>([...dead, ...out.out.map((o) => o.driverId)]);
  const picture = playoffPicture(season, raceNumber, table, (id) => !lost.has(id));

  const books = ws.books ?? null;
  const contracts = filterContracts(books?.contracts ?? {}, raceNumber, Boolean(books?.settled));
  const changes = books?.settled ? [] : (books?.sponsorChanges ?? []).filter((c) => c.raceNumber === raceNumber);

  const names = new Map<string, string>();
  for (const s of seasons) for (const c of s.roster) names.set(c.driverId, c.name);
  for (const d of table.drivers) names.set(d.driverId, d.name);
  for (const f of run.race.finishingOrder) names.set(f.driverId, f.name);
  const nameOf = (id: string): string => names.get(id) ?? people.registry.byId[id]?.name ?? id;
  // Two Turnages in the field: a surname alone would not say which one.
  const bySurname = new Map<string, Set<string>>();
  // Only men on the books by race N: a rookie signed later must not change how an earlier week reads.
  for (const id of new Set([...table.drivers.map((d) => d.driverId), ...run.race.finishingOrder.map((f) => f.driverId), ...run.absences.map((a) => a.driverId)])) {
    const sn = surname(nameOf(id));
    if (!bySurname.has(sn)) bySurname.set(sn, new Set());
    bySurname.get(sn)!.add(id);
  }
  const short = (id: string): string => {
    const sn = surname(nameOf(id));
    return (bySurname.get(sn)?.size ?? 0) > 1 ? nameOf(id) : sn;
  };
  // His team as of race N (the standings remember it), not after a later transfer.
  const teamOf = (id: string): string | null => {
    const teamId = table.byDriver[id]?.teamId ?? null;
    return (teamId ? season.teams.find((t) => t.teamId === teamId)?.name : null) ?? null;
  };
  const sponsorOf = (id: string): string | null => {
    const p = contracts[id]?.primary;
    return p?.sponsorName ?? null;
  };
  return {
    key: `${people.leagueKey}|s${seasonNumber}|r${raceNumber}`,
    seasonKey: `${people.leagueKey}|s${seasonNumber}`,
    seasonNumber,
    raceNumber,
    seasons,
    season,
    run,
    races,
    table,
    prevTable,
    next,
    schedule: ws.schedule,
    people,
    peoplePrev,
    out,
    unavailable,
    lost,
    picture,
    contracts,
    sponsorChanges: changes,
    // A settled season reads its contracts only, whether or not this copy still
    // has the weekly log, so its back Wires print the same before and after a save.
    sponsorChangesHeld: !books?.settled && Array.isArray(books?.sponsorChanges),
    booksSettled: Boolean(books?.settled),
    nameOf,
    short,
    track: trackInProse(run.scheduled.trackName),
    trackShort: shortTrack(run.scheduled.trackName),
    teamOf,
    sponsorOf,
    priors: league.priors ?? {},
  };
}

/**
 * The contracts as they stood after race N: nothing signed after race N, and —
 * for a settled season's year-end books — no primary signed over the winter
 * for NEXT season (it was never on the car this year).
 */
function filterContracts(
  all: Readonly<Record<string, DriverFinanceState>>,
  raceNumber: number,
  settled: boolean,
): Record<string, DriverFinanceState> {
  const out: Record<string, DriverFinanceState> = {};
  for (const [id, dfs] of Object.entries(all)) {
    const primary = dfs.primary && !(settled && dfs.primary.signedInWinter) && dfs.primary.signedRace <= raceNumber ? dfs.primary : null;
    out[id] = {
      ...dfs,
      primary,
      major: dfs.major.filter((c) => c.signedRace <= raceNumber),
      associate: dfs.associate.filter((c) => c.signedRace <= raceNumber),
    };
  }
  return out;
}
